import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Plus, Clock, CheckCircle, XCircle, AlertCircle, 
  ArrowRight, Lightbulb, Send
} from 'lucide-react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import { api } from '../lib/auth';
import { toast } from 'sonner';
import { formatDate } from '../lib/utils';
import { LogoIcon } from '../components/Logo';

const URGENCY_OPTIONS = [
  { value: 'flexible', label: 'Flexible - No rush', color: 'text-gray-400' },
  { value: 'normal', label: 'Normal - Within 1-2 weeks', color: 'text-blue-400' },
  { value: 'urgent', label: 'Urgent - ASAP', color: 'text-orange-400' },
];

const BUDGET_OPTIONS = [
  { value: 'under_500', label: 'Under $500' },
  { value: '500_1000', label: '$500 - $1,000' },
  { value: '1000_2500', label: '$1,000 - $2,500' },
  { value: '2500_5000', label: '$2,500 - $5,000' },
  { value: 'over_5000', label: 'Over $5,000' },
  { value: 'not_sure', label: 'Not sure yet' },
];

const STATUS_STYLES = {
  pending: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', icon: Clock },
  reviewed: { bg: 'bg-blue-500/20', text: 'text-blue-400', icon: AlertCircle },
  approved: { bg: 'bg-green-500/20', text: 'text-green-400', icon: CheckCircle },
  declined: { bg: 'bg-red-500/20', text: 'text-red-400', icon: XCircle },
};

export default function ServiceRequestsPage() {
  const [requests, setRequests] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    service_name: '',
    description: '',
    department_preference: '',
    budget_range: '',
    urgency: 'normal'
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [requestsRes, deptsRes] = await Promise.all([
        api.getServiceRequests(),
        api.getDepartments()
      ]);
      setRequests(requestsRes.data);
      setDepartments(deptsRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.service_name.trim() || !formData.description.trim()) {
      toast.error('Please fill in the service name and description');
      return;
    }

    setSubmitting(true);
    try {
      await api.createServiceRequest(formData);
      toast.success('Service request submitted! We will review it shortly.');
      setFormData({
        service_name: '',
        description: '',
        department_preference: '',
        budget_range: '',
        urgency: 'normal'
      });
      setShowForm(false);
      loadData();
    } catch (error) {
      toast.error('Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <Lightbulb className="w-8 h-8 text-primary" />
              <h1 className="font-heading text-3xl font-bold text-white">
                Service Requests
              </h1>
            </div>
            <Button 
              onClick={() => setShowForm(!showForm)} 
              className="btn-primary"
              data-testid="new-request-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Request
            </Button>
          </div>
          <p className="text-gray-400">
            Can&apos;t find a service you need? Request it here and we&apos;ll see if we can add it!
          </p>
        </motion.div>

        {/* Request Form */}
        {showForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="glass rounded-2xl p-6 mb-8"
          >
            <h2 className="font-heading text-xl font-semibold text-white mb-6">
              Request a New Service
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="service_name" className="text-gray-300">
                  Service Name <span className="text-red-400">*</span>
                </Label>
                <Input
                  id="service_name"
                  value={formData.service_name}
                  onChange={(e) => setFormData({ ...formData, service_name: e.target.value })}
                  placeholder="e.g., Custom AI Chatbot Development"
                  className="input-dark"
                  data-testid="service-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description" className="text-gray-300">
                  Description <span className="text-red-400">*</span>
                </Label>
                <textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe the service you need in detail. What problem are you trying to solve?"
                  className="w-full h-32 px-4 py-3 bg-black/20 border border-white/10 rounded-xl text-white placeholder:text-gray-600 focus:border-primary/50 focus:outline-none resize-none"
                  data-testid="description-input"
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-gray-300">Preferred Department</Label>
                  <Select
                    value={formData.department_preference}
                    onValueChange={(value) => setFormData({ ...formData, department_preference: value })}
                  >
                    <SelectTrigger className="input-dark" data-testid="department-select">
                      <SelectValue placeholder="Select a department (optional)" />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-white/10">
                      <SelectItem value="not_sure">Not sure</SelectItem>
                      {departments.map((dept) => (
                        <SelectItem key={dept.id} value={dept.id}>
                          {dept.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-gray-300">Budget Range</Label>
                  <Select
                    value={formData.budget_range}
                    onValueChange={(value) => setFormData({ ...formData, budget_range: value })}
                  >
                    <SelectTrigger className="input-dark" data-testid="budget-select">
                      <SelectValue placeholder="Select budget (optional)" />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-white/10">
                      {BUDGET_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-300">Urgency</Label>
                <Select
                  value={formData.urgency}
                  onValueChange={(value) => setFormData({ ...formData, urgency: value })}
                >
                  <SelectTrigger className="input-dark" data-testid="urgency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-white/10">
                    {URGENCY_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        <span className={opt.color}>{opt.label}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex gap-4">
                <Button 
                  type="button" 
                  variant="ghost" 
                  onClick={() => setShowForm(false)}
                  className="text-gray-400"
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  className="btn-primary flex-1"
                  disabled={submitting}
                  data-testid="submit-request-btn"
                >
                  {submitting ? (
                    <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Submit Request
                    </>
                  )}
                </Button>
              </div>
            </form>
          </motion.div>
        )}

        {/* Requests List */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          <h2 className="font-heading text-xl font-semibold text-white mb-4">
            Your Requests
          </h2>

          {loading ? (
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-24 bg-white/5 rounded-xl animate-pulse" />
              ))}
            </div>
          ) : requests.length === 0 ? (
            <div className="text-center py-16 glass rounded-2xl">
              <LogoIcon size="lg" className="mx-auto mb-4 opacity-50" />
              <p className="text-gray-400 mb-4">No service requests yet</p>
              <Button onClick={() => setShowForm(true)} className="btn-secondary">
                <Plus className="w-4 h-4 mr-2" />
                Submit Your First Request
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {requests.map((request, i) => {
                const statusStyle = STATUS_STYLES[request.status] || STATUS_STYLES.pending;
                const StatusIcon = statusStyle.icon;
                
                return (
                  <motion.div
                    key={request.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.05 }}
                    className="glass rounded-xl p-5"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-semibold text-white text-lg">
                          {request.service_name}
                        </h3>
                        <p className="text-sm text-gray-500">
                          Submitted {formatDate(request.created_at)}
                        </p>
                      </div>
                      <div className={`flex items-center gap-2 px-3 py-1 rounded-full ${statusStyle.bg}`}>
                        <StatusIcon className={`w-4 h-4 ${statusStyle.text}`} />
                        <span className={`text-sm font-medium capitalize ${statusStyle.text}`}>
                          {request.status}
                        </span>
                      </div>
                    </div>
                    
                    <p className="text-gray-400 text-sm mb-3 line-clamp-2">
                      {request.description}
                    </p>
                    
                    <div className="flex flex-wrap gap-3 text-xs">
                      {request.budget_range && (
                        <span className="px-2 py-1 rounded bg-white/5 text-gray-400">
                          Budget: {BUDGET_OPTIONS.find(b => b.value === request.budget_range)?.label || request.budget_range}
                        </span>
                      )}
                      <span className={`px-2 py-1 rounded bg-white/5 ${URGENCY_OPTIONS.find(u => u.value === request.urgency)?.color || 'text-gray-400'}`}>
                        {URGENCY_OPTIONS.find(u => u.value === request.urgency)?.label || request.urgency}
                      </span>
                    </div>
                    
                    {request.admin_notes && (
                      <div className="mt-4 p-3 rounded-lg bg-primary/10 border border-primary/20">
                        <p className="text-sm text-gray-300">
                          <span className="font-medium text-primary">Admin Response:</span> {request.admin_notes}
                        </p>
                      </div>
                    )}
                  </motion.div>
                );
              })}
            </div>
          )}
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="mt-8 text-center"
        >
          <p className="text-gray-500 text-sm mb-4">
            Looking for existing services?
          </p>
          <Link to="/marketplace">
            <Button variant="ghost" className="text-primary">
              Browse Marketplace <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </motion.div>
      </div>
    </DashboardLayout>
  );
}
